/**
 * ProgressTabDesktop — split-screen desktop layout.
 * Spec §4.2, visual ref 01-desktop.html.
 *
 * Layout:
 *   HeadlineBand (then vs now scores + citation rates)
 *   SplitScreen (ScoreCards)
 *   PillarSection × 3 (SEO / AEO / GEO drill-downs)
 *   AttributionGrid
 *   RollupChart
 */

import { useState } from 'react';
import { ScoreTag } from './ScoreTag';
import { ScoreCard } from './ScoreCard';
import { AttributionCard } from './AttributionCard';
import { RollupChart } from './RollupChart';
import { HeadlineBand } from './HeadlineBand';
import { PlatformBreakdown } from './PlatformBreakdown';
import { TruncationBanner } from './TruncationBanner';
import { buildDrilldownGroups } from './buildDrilldownGroups';
import type { DateRange } from '../../hooks/useProgressData';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuditResults = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Attribution = Record<string, any>;

interface Props {
  status: 'loading' | 'success' | 'error';
  error?: { message: string; retry: () => void };
  thenAudit: AuditResults | null;
  nowAudit: AuditResults | null;
  windowAudits: AuditResults[];
  attributions: Attribution[];
  thenCitationRate: number | null;
  nowCitationRate: number | null;
  thenAiMentionShare: number | null;
  nowAiMentionShare: number | null;
  headlineNarrative: string | null;
  issuesFixed: number;
  visitsGained: number;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  domainId: string;
  customerTier: string;
}

function ScoresExplainerToggle() {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('progress.scoresExplainer.open') === 'true'; }
    catch { return false; }
  });

  function toggle() {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem('progress.scoresExplainer.open', String(next)); } catch { /* ignore */ }
  }

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={toggle}
        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
      >
        How are these scores calculated?
        <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-2 text-xs text-slate-500 leading-relaxed max-w-2xl bg-slate-50 rounded-lg p-3">
          <strong className="text-slate-700">SEO</strong> measures technical health, on-page signals, and structured data. <strong className="text-slate-700">AEO</strong> measures how well your pages answer questions for AI engines — FAQ schema, direct answers, content structure. <strong className="text-slate-700">GEO</strong> measures your geographic and brand authority for local AI queries — citation rates, business directory listings, and freshness.
        </div>
      )}
    </div>
  );
}

function AuditCard({ label, audit, citationRate }: { label: string; audit: AuditResults | null; citationRate: number | null }) {
  if (!audit) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <p className="text-sm text-slate-400 font-medium uppercase tracking-wider mb-4">{label}</p>
        <p className="text-slate-400 text-sm">No audit</p>
      </div>
    );
  }
  const ts = audit.completedAt ?? audit.startedAt ?? '';
  const dateLabel = ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">{label}</p>
        <p className="text-xs text-slate-400">{dateLabel}</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">AI Visibility</p>
          <ScoreTag score={audit.overall?.value ?? null} kind="overall" size="lg" />
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-blue-400 mb-1">SEO</p>
          <ScoreTag score={audit.scores?.seo?.value ?? null} kind="seo" size="md" />
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1">AEO</p>
          <ScoreTag score={audit.scores?.aeo?.value ?? null} kind="aeo" size="md" />
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">GEO</p>
          <ScoreTag score={audit.scores?.geo?.value ?? null} kind="geo" size="md" />
        </div>
      </div>
      {citationRate !== null && (
        <p className="mt-3 text-xs text-slate-400">
          Citation rate: <span className="font-semibold text-emerald-600">{citationRate}%</span>
        </p>
      )}
    </div>
  );
}

function scoreDelta(then: number | null, now: number | null): { direction: 'improved' | 'worsened' | 'unchanged' | 'incomparable'; display: string } {
  if (then === null || now === null) return { direction: 'incomparable', display: '—' };
  const diff = now - then;
  if (diff === 0) return { direction: 'unchanged', display: '+0' };
  return { direction: diff > 0 ? 'improved' : 'worsened', display: `${diff > 0 ? '+' : ''}${diff}` };
}

export function ProgressTabDesktop({
  domainId,
  thenAudit,
  nowAudit,
  windowAudits,
  attributions,
  thenCitationRate,
  nowCitationRate,
  thenAiMentionShare,
  nowAiMentionShare,
  headlineNarrative,
  issuesFixed,
  visitsGained,
  dateRange,
  setDateRange,
  error,
  customerTier,
}: Props) {
  const [expandedScore, setExpandedScore] = useState<'seo' | 'aeo' | 'geo' | null>(null);

  function toggleScore(s: 'seo' | 'aeo' | 'geo') {
    setExpandedScore((prev) => (prev === s ? null : s));
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
        <p className="text-rose-700 font-medium">{error.message}</p>
        <button onClick={error.retry} className="mt-3 text-sm text-rose-600 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Headline band — 5-stat 3-2 grid (§A) */}
      <HeadlineBand
        issuesFixed={issuesFixed}
        visitsGained={visitsGained}
        thenScore={thenAudit?.overall?.value ?? null}
        nowScore={nowAudit?.overall?.value ?? null}
        thenCitationRate={thenCitationRate}
        nowCitationRate={nowCitationRate}
        thenAiMentionShare={thenAiMentionShare}
        nowAiMentionShare={nowAiMentionShare}
        narrative={headlineNarrative}
      />

      {/* Date range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Progress</h2>
          <p className="text-sm text-slate-400 mt-0.5">Comparing scores across your selected window</p>
        </div>
        {/* Date range display (full DatePicker in Sprint 2.8b.7) */}
        <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-1.5" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {dateRange.from} → {dateRange.to}
          {dateRange.preset !== 'custom' && (
            <span className="ml-2 text-xs text-slate-400">({dateRange.preset})</span>
          )}
        </div>
      </div>

      <ScoresExplainerToggle />

      {/* Truncation upsell banner (A5.3) */}
      <TruncationBanner
        truncationBlock={nowAudit?.crawlerTruncation ?? null}
        customerTier={customerTier}
        dismissKey={domainId}
      />

      {/* Split screen — then vs now */}
      <div className="grid grid-cols-2 gap-5">
        <AuditCard label="Where you were" audit={thenAudit} citationRate={thenCitationRate} />
        <AuditCard label="Where you are now" audit={nowAudit} citationRate={nowCitationRate} />
      </div>

      {/* Pillar sections — SEO / AEO / GEO */}
      {(['seo', 'aeo', 'geo'] as const).map((score) => (
        <ScoreCard
          key={score}
          score={score}
          thenValue={thenAudit?.scores?.[score]?.value ?? null}
          nowValue={nowAudit?.scores?.[score]?.value ?? null}
          delta={scoreDelta(thenAudit?.scores?.[score]?.value ?? null, nowAudit?.scores?.[score]?.value ?? null)}
          expanded={expandedScore === score}
          onToggle={() => toggleScore(score)}
          drilldownGroups={buildDrilldownGroups(
            score,
            thenAudit,
            nowAudit,
            score === 'geo'
              ? { then: thenAudit?.offDomain, now: nowAudit?.offDomain }
              : undefined,
          )}
          confidence={(nowAudit?.scores?.[score]?.confidence ?? 'medium') as 'high' | 'medium' | 'low'}
        />
      ))}

      {/* Per-platform citation rate breakdown (GEO) */}
      {(nowAudit?.geoSweep?.platforms ?? []).length > 0 && (
        <PlatformBreakdown
          platforms={nowAudit?.geoSweep?.platforms ?? []}
          thenPlatforms={thenAudit?.geoSweep?.platforms ?? []}
          customerTier={customerTier}
        />
      )}

      {/* Attributions */}
      {attributions.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              Fix attributions
              <span className="ml-2 text-sm text-slate-400 font-normal">
                ({attributions.length} {attributions.length === 1 ? 'fix' : 'fixes'} in window)
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {attributions.map((a) => (
              <AttributionCard key={a.id} attribution={a} />
            ))}
          </div>
        </section>
      )}

      {/* Rollup chart */}
      <section>
        <h3 className="text-base font-semibold text-slate-900 mb-4">Score history</h3>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <RollupChart audits={windowAudits} attributions={attributions} />
        </div>
      </section>
    </div>
  );
}
